import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

import { DEFAULT_TOAST_TIMEOUT, TEST_ID } from './constants';
import type {
    FunctionComponent,
    Notification,
    Props,
    ResolveFunction,
    ToastContextType
} from './types';

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider: FunctionComponent<Props> = ({ children, testId = TEST_ID }) => {
    const [toast, setToast] = useState<Notification | null>(null);
    const timeoutIdRef = useRef<NodeJS.Timeout | null>(null);
    const resolveRef = useRef<ResolveFunction>(null);

    const clearToastTimeout = useCallback(() => {
        if (timeoutIdRef.current) {
            clearTimeout(timeoutIdRef.current);
            timeoutIdRef.current = null;
        }
    }, []);

    const resolvePromise = useCallback((value: boolean) => {
        if (resolveRef.current) {
            resolveRef.current(value);
            resolveRef.current = null;
        }
    }, []);

    const dismissToast = useCallback(
        (value: boolean) => {
            clearToastTimeout();
            resolvePromise(value);
            setToast(null);
        },
        [clearToastTimeout, resolvePromise]
    );

    useEffect(
        () => () => {
            clearToastTimeout();
            resolvePromise(false);
        },
        [clearToastTimeout, resolvePromise]
    );

    const broadcastToast = useCallback(
        ({
            text,
            time = DEFAULT_TOAST_TIMEOUT,
            type
        }: Notification & { time?: number }): Promise<boolean> =>
            new Promise(resolve => {
                if (timeoutIdRef.current || resolveRef.current) {
                    dismissToast(false);
                }

                resolveRef.current = resolve;
                setToast({ text, type });
                timeoutIdRef.current = setTimeout(() => dismissToast(false), time);
            }),
        [dismissToast]
    );

    const handleRetry = useCallback(() => {
        dismissToast(true);
    }, [dismissToast]);

    const clearToast = useCallback(() => {
        dismissToast(false);
    }, [dismissToast]);

    return (
        <ToastContext.Provider
            data-testid={testId}
            value={{ broadcastToast, clearToast, handleRetry, toast }}>
            {children}
        </ToastContext.Provider>
    );
};

export const useToastContext = () => {
    const context = useContext(ToastContext);

    if (context === undefined) {
        throw new Error('useToastContext must be used within a ToastProvider');
    }

    return context;
};
